// Service pour récupérer les jeux Steam et leur temps de jeu
import { supabase } from '@/integrations/supabase/client';

export interface SteamPlaytimeGame {
	appid: number;
	name: string;
	playtime_forever: number; // en minutes
	playtime_2weeks?: number; // en minutes
	img_icon_url: string;
}

export interface SteamAchievement {
	name: string;
	achieved: boolean;
	unlocktime?: number;
}

export interface SteamAchievementsData {
	gameName: string;
	achievements: SteamAchievement[];
}

export interface SteamAchievementStats {
	total: number;
	unlocked: number;
	percentage: number;
}

export interface SteamGamesResponse {
	response?: {
		game_count: number;
		games?: SteamPlaytimeGame[];
	};
}

// Récupérer tous les jeux Steam d'un utilisateur
export async function getUserSteamGames(userId: string): Promise<SteamPlaytimeGame[]> {
	try {
		// Récupérer le steam_id de l'utilisateur
		const { data: profile, error: profileError } = await supabase
			.from('profiles')
			.select('steam_id')
			.eq('id', userId)
			.single();

		if (profileError || !profile?.steam_id) {
			return [];
		}

		// Utiliser le proxy Supabase Edge Function
		const { data, error } = await supabase.functions.invoke('steam-proxy', {
			body: { action: 'getOwnedGames', steamId: profile.steam_id }
		});

		if (error) {
			console.error('Erreur Steam Proxy:', error);
			return [];
		}

		// Gérer la structure de réponse du proxy (qui renvoie le JSON de Steam)
		let games = null;
		const responseData = data.response || data;

		if (responseData.games) {
			games = responseData.games;
		}

		if (games && Array.isArray(games) && games.length > 0) {
			return games.sort((a: any, b: any) => b.playtime_forever - a.playtime_forever);
		}

		return [];
	} catch (error) {
		console.error('Erreur lors de la récupération des jeux Steam:', error);
		return [];
	}
}

// Formater le temps de jeu en heures
export function formatPlaytime(minutes: number): string {
	if (minutes < 60) return `${minutes}m`;
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;
	if (remainingMinutes === 0) return `${hours}h`;
	return `${hours}h ${remainingMinutes}m`;
}

// Générer l'URL de l'image d'icône Steam
export function getSteamIconUrl(iconUrl: string, appId: number): string {
	return `https://media.steampowered.com/steamcommunity/public/images/apps/${appId}/${iconUrl}.jpg`;
}

// Récupérer les achievements d'un utilisateur pour un jeu spécifique
export async function getUserAchievementsForGame(userId: string, appId: number): Promise<SteamAchievementsData | null> {
	try {
		// Récupérer le steam_id de l'utilisateur
		const { data: profile, error: profileError } = await supabase
			.from('profiles')
			.select('steam_id')
			.eq('id', userId)
			.single();

		if (profileError || !profile?.steam_id) {
			return null;
		}

		// Utiliser le proxy Supabase Edge Function
		const { data, error } = await supabase.functions.invoke('steam-proxy', {
			body: { action: 'getPlayerAchievements', steamId: profile.steam_id, appId }
		});

		if (error) {
			// console.error('Erreur Steam Proxy Achievements:', error);
			return null;
		}

		const responseData = data.response || data;

		// La réponse de l'API Steam via le proxy
		if (responseData.playerstats?.success && responseData.playerstats?.achievements) {
			return {
				gameName: responseData.playerstats.gameName || '',
				achievements: responseData.playerstats.achievements
			};
		}

		// Si success est true mais pas d'achievements (jeu sans achievements ou pas encore joués)
		if (responseData.playerstats?.success && !responseData.playerstats?.achievements) {
			return {
				gameName: responseData.playerstats.gameName || '',
				achievements: []
			};
		}

		return null;
	} catch (error) {
		// console.error('Erreur lors de la récupération des achievements:', error);
		return null;
	}
}

// Calculer les statistiques d'achievements
export function calculateAchievementStats(achievementsData: SteamAchievementsData): SteamAchievementStats {
	const total = achievementsData.achievements.length;
	const unlocked = achievementsData.achievements.filter(a => a.achieved).length;
	const percentage = total > 0 ? Math.round((unlocked / total) * 100) : 0;

	return {
		total,
		unlocked,
		percentage
	};
}

// Récupérer les achievements d'un utilisateur pour tous ses jeux
export async function getUserAchievementsForAllGames(userId: string, steamGames: SteamPlaytimeGame[]): Promise<Map<number, SteamAchievementStats>> {
	const achievementsMap = new Map<number, SteamAchievementStats>();

	// Limiter à 10 jeux pour ne pas surcharger l'API
	const limitedGames = steamGames.slice(0, 10);

	for (const game of limitedGames) {
		try {
			const achievementsData = await getUserAchievementsForGame(userId, game.appid);
			if (achievementsData) {
				const stats = calculateAchievementStats(achievementsData);
				achievementsMap.set(game.appid, stats);
			}
		} catch (error) {
			// Ignorer les erreurs pour un jeu spécifique
			continue;
		}
	}

	return achievementsMap;
}

// Récupérer les données complètes Steam (playtime + achievements) pour un jeu
export async function getSteamApiData(userId: string, appId: number): Promise<{
	total_achievements: number;
	unlocked_achievements: number;
	playtime_forever: number;
	playtime_2weeks: number;
} | null> {
	try {
		// Récupérer le steam_id de l'utilisateur
		const { data: profile, error: profileError } = await supabase
			.from('profiles')
			.select('steam_id')
			.eq('id', userId)
			.single();

		if (profileError || !profile?.steam_id) {
			return null;
		}

		const apiKey = import.meta.env.VITE_STEAM_API_KEY;
		if (!apiKey) {
			return null;
		}

		// Récupérer playtime
		const playtimeData = await getPlaytimeForGame(userId, appId);
		const playtimeForever = playtimeData?.playtime || 0;
		const playtime2Weeks = playtimeData?.gameInfo?.playtime_2weeks || 0;

		// Récupérer achievements
		const achievementsData = await getUserAchievementsForGame(userId, appId);
		const totalAchievements = achievementsData?.achievements.length || 0;
		const unlockedAchievements = achievementsData?.achievements.filter(a => a.achieved).length || 0;

		return {
			total_achievements: totalAchievements,
			unlocked_achievements: unlockedAchievements,
			playtime_forever: playtimeForever,
			playtime_2weeks: playtime2Weeks
		};
	} catch (error) {
		return null;
	}
}

// Récupérer le temps de jeu pour un jeu spécifique
export async function getPlaytimeForGame(userId: string, appId: number): Promise<{ hasGame: boolean; playtime: number; gameInfo: any } | null> {
	try {
		// Récupérer le steam_id de l'utilisateur
		const { data: profile, error: profileError } = await supabase
			.from('profiles')
			.select('steam_id')
			.eq('id', userId)
			.single();

		if (profileError || !profile?.steam_id) {
			// console.log('Pas de Steam ID trouvé');
			return null;
		}

		// Utiliser le proxy Supabase Edge Function
		const { data, error } = await supabase.functions.invoke('steam-proxy', {
			body: { action: 'getOwnedGames', steamId: profile.steam_id, appId }
		});

		if (error) {
			// console.warn('Steam API proxy error (non-critical):', error);
			return null;
		}

		const responseData = data.response || data;

		let games = null;

		if (responseData.games) {
			games = responseData.games;
		}

		if (games && Array.isArray(games) && games.length > 0) {
			const game = games.find((g: any) => g.appid === appId);
			if (game) {
				return {
					hasGame: true,
					playtime: game.playtime_forever,
					gameInfo: game
				};
			}
		}

		return {
			hasGame: false,
			playtime: 0,
			gameInfo: null
		};
	} catch (error) {
		// console.error('Erreur lors de la récupération:', error);
		return null;
	}
}
